import { FontSizeOption } from '../contexts/FontSizeContext';

/**
 * Get font size classes based on the selected font size option.
 * These classes are relative to the base responsive sizes.
 * 
 * For responsive patterns like "text-sm sm:text-base":
 * - small: "text-xs sm:text-sm" (one step smaller)
 * - medium: "text-sm sm:text-base" (default/base)
 * - large: "text-base sm:text-lg" (one step larger)
 */

/**
 * Helper function for common font size patterns (table data cells)
 * Base: text-sm sm:text-base
 */
export const getTextSizeClass = (fontSize: FontSizeOption): string => {
  switch (fontSize) {
    case 'small':
      return 'text-xs sm:text-sm';
    case 'medium':
      return 'text-sm sm:text-base';
    case 'large':
      return 'text-base sm:text-lg';
  }
};

/**
 * Helper for header text sizes (table headers)
 * Base: text-xs
 */
export const getHeaderTextSizeClass = (fontSize: FontSizeOption): string => {
  switch (fontSize) {
    case 'small':
      return 'text-[10px] sm:text-xs';
    case 'medium':
      return 'text-xs';
    case 'large':
      return 'text-xs sm:text-sm';
  }
};

/**
 * Helper for small text (like labels, captions, status bars)
 * Base: text-xs
 */
export const getSmallTextSizeClass = (fontSize: FontSizeOption): string => {
  switch (fontSize) {
    case 'small':
      return 'text-[10px] sm:text-xs';
    case 'medium':
      return 'text-xs';
    case 'large':
      return 'text-xs sm:text-sm';
  }
};

/**
 * Helper for tight, multi-line table cell text where space is limited,
 * but we still want a *visible* step from small→medium and medium→large
 * on typical (sm+) screens.
 *
 * Base (medium): text-xs sm:text-sm
 */
export const getTightCellTextSizeClass = (fontSize: FontSizeOption): string => {
  switch (fontSize) {
    case 'small':
      return 'text-[11px] sm:text-xs';
    case 'medium':
      return 'text-xs sm:text-sm';
    case 'large':
      return 'text-sm sm:text-base';
  }
};

/**
 * Helper for statistic label text
 * Base: text-sm sm:text-base
 */
export const getStatisticLabelSizeClass = (fontSize: FontSizeOption): string => {
  switch (fontSize) {
    case 'small':
      return 'text-xs sm:text-sm';
    case 'medium':
      return 'text-sm sm:text-base';
    case 'large':
      return 'text-base sm:text-lg';
  }
};

/**
 * Helper for display/hero text sizes (large headings)
 * Base: text-4xl sm:text-5xl lg:text-6xl
 */
export const getDisplayTextSizeClass = (fontSize: FontSizeOption): string => {
  switch (fontSize) {
    case 'small':
      return 'text-3xl sm:text-4xl lg:text-5xl';
    case 'medium':
      return 'text-4xl sm:text-5xl lg:text-6xl';
    case 'large':
      return 'text-5xl sm:text-6xl lg:text-7xl';
  }
};

/**
 * Helper for page title sizes (h1)
 * Base: text-3xl sm:text-4xl
 */
export const getPageTitleSizeClass = (fontSize: FontSizeOption): string => {
  switch (fontSize) {
    case 'small':
      return 'text-2xl sm:text-3xl';
    case 'medium':
      return 'text-3xl sm:text-4xl';
    case 'large':
      return 'text-4xl sm:text-5xl';
  }
};

/**
 * Helper for section heading sizes (h2)
 * Base: text-2xl
 */
export const getSectionHeadingSizeClass = (fontSize: FontSizeOption): string => {
  switch (fontSize) {
    case 'small':
      return 'text-xl sm:text-2xl';
    case 'medium':
      return 'text-2xl';
    case 'large':
      return 'text-2xl sm:text-3xl';
  }
};

/**
 * Helper for subsection heading sizes (h3)
 * Base: text-xl
 */
export const getSubsectionHeadingSizeClass = (fontSize: FontSizeOption): string => {
  switch (fontSize) {
    case 'small':
      return 'text-lg sm:text-xl';
    case 'medium':
      return 'text-xl';
    case 'large':
      return 'text-xl sm:text-2xl';
  }
};

